import React, {
    useState,
    useEffect,
    useMemo,
    useRef,
    useCallback,
    useLayoutEffect,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, Link } from "react-router-dom";
import SecureProperty from "./SecureProperty";
import AuctionTimer from "./AuctionTimer";
import HighestBid from "./HighestBid";
import ComparableProperties from "./ComparableProperties";
import CustomButton from "./CustomButton";
import HelpSection from "./HelpSection";
import Shimmer from "../../../common/shimmer/Shimmer";
import SocketService from "../../../../Service/SocketService";
import PlaceBidModal from "./modals/PlaceBidModal";
import RegisterInterestModal from "./modals/RegisterInterestModal";
import VerifyAccountModal from "./modals/VerifyAccountModal";
import BidHistory from "./BidHistory";
import {
    ABUDHABICITYID,
    accountStatus,
    BID_APPROVAL_STATUS_MAP,
    DOMAIN,
} from "../../../../utils/constants";
import useDidMountEffect from "../../../hooks/useDidMountEffect";
import {
    setModalStep,
    setShowPlaceBidModal,
} from "../../../../redux/slice/sellerSlice";
import { removeQueryParam } from "../../../../utils";
import useIPAddress from "../../../hooks/useIPAddress";
import { handleSiteLoader } from "../../../../redux/action/authAction";
import useTranslationHook from "../../../hooks/useTranslationHook";
import StartBuyingProcessModal from "../../../common/StartBuyingProcessModal";
import ForefitModal from "../../../common/ForefitModal";
import { handleContactUsModal } from "../../../../redux/slice/modalSlice";
import {
    createSlug,
    formatNumberByLocale,
    formatPrice,
} from "../../../../helpers";
import { guessDateTime } from "../../../../utils/dateUtils";
import { toast } from "react-toastify";
import { fetchPropertyBidsHistory } from "../../../../redux/action/bidHistoryAction";
import { handleCalculateFinalPrice } from "../../../../redux/slice/buyerSlice";
import PriceCalculator from "./PriceCalculator";
import moment from "moment";
import { fetchLots } from "../../../../redux/action/liveAuctionAction";
import { calFinalPrice } from "../../../../redux/action/buyerAction";
const AuctionBidding = ({ onDataChange }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const isSeller = useSelector((state) => state.profile.account);
    const { t } = useTranslationHook();
    const lang = useSelector((state) => state.translation.lang);
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

    const {
        id,
        id: property_id,
        property_auction_data,
        is_register_interest,
        is_auto_bid_enabled,
        reg_auto_bid_amount,
        min_bid_amount,
        reg_bid_amount: bid_amount,
        is_approved,
        registration_id,
        is_bid_transaction,
        reg_transaction_status,
        purchase_forefit_status,
        property_name,
        country,
        is_remember_interest,
        city,
        state,
        community,
        community_ar,
        agent_id,
        property_for,
        property_status,
        property_setting,
        is_registered,
        deposit_amount,
        registration_approved,
        property_name_ar,
        is_payment_completed,
        registration_open_date,
        session_id,
        session_status,
        lot_status,
        lot_pause_resume,
        session_start_date,
        lot_id,
        purchase_deadline,
        purchase_days_remaining,
    } = useSelector((state) => state.seller.property.propertyData);

    const [isPurchased, setIsPurchased] = useState(false);
    const [isForeFitted, setIsForeFitted] = useState(false);
    useEffect(() => {
        if (purchase_forefit_status) {
            setIsPurchased(purchase_forefit_status === 1);
            setIsForeFitted(purchase_forefit_status === 2);
        }
        //eslint-disable-next-line
    }, [purchase_forefit_status]);

    const { showPlaceBidModal } = useSelector((state) => state.seller.property);
    const { ip } = useIPAddress();
    const user = useSelector((state) => state.auth.user);
    const { isConnected } = useSelector((state) => state.socket);
    const buyerPriceCalculator = useSelector(
        (state) => state.buyer.buyerPriceCalculator
    );
    const lots = useSelector((state) => state.liveAuction.lots);

    const [isRegisteredInterset, setIsRegisteredInterest] =
        useState(is_register_interest);

    const [biddingData, setBiddingData] = useState(null);
    const [auctionStatus, setAuctionStatus] = useState(null);
    const [showRegisterInterestModal, setShowRegisterInterestModal] =
        useState(false);
    const [showPopUp, setShowPopUp] = useState(true);
    const [accountVerifiedModal, setAccountVerifiedModal] = useState(false);
    const [showStartBuyingModal, setShowStartBuyingModal] = useState(false);
    const [showForefitModal, setShowForefitModal] = useState(false);
    const [approvalStatus, setApprovalStatus] = useState(registration_approved);
    const [isFixed, setIsFixed] = useState(false);

    const prevAuctionStatus = useRef(null);

    const auction_id = property_auction_data[0]?.id;
    const bid_increments = property_auction_data[0]?.bid_increments;
    const start_price = property_auction_data[0]?.start_price;
    const start_date = property_auction_data[0]?.start_date;
    const end_date = property_auction_data[0]?.end_date;
    // visibility 2 = private auction. Private (non-live-room) auctions skip
    // "Register Interest" and offer "Register As Bidder" before the start.
    const isPrivateAuction =
        property_auction_data[0]?.visibility === 2 && property_for !== 2;
    const reserveAmount = biddingData?.reserve_amount;
    const highBidAmt = biddingData?.high_bid_amt;
    const isReserveMet = reserveAmount <= highBidAmt;
    const is_selected_highest_bid = biddingData?.is_selected_highest_bid;
    const offerer_user_id = biddingData?.offerer_user_id;
    const listing_status_name = biddingData?.listing_status_name;
    const isBidApproved = biddingData?.is_approved;
    const isRegistrationAllowed =
        (registration_open_date &&
            moment().isSameOrAfter(registration_open_date)) ||
        (!registration_open_date &&
            moment().isBetween(start_date, end_date, null, "[]"));

    const isRegistrationApproved =
        is_registered && approvalStatus && isBidApproved === 2;

    const showFinalHighestBidderMessage =
        parseInt(user?.user_id) === biddingData?.max_bidder_user_id &&
        isSeller === 0 &&
        (isReserveMet || is_selected_highest_bid) &&
        auctionStatus === 3;

    const purchaseDeadline = purchase_deadline
        ? guessDateTime(purchase_deadline, "MMM DD, YYYY, hh:mm A", 0, lang)
        : "";
    const purchaseDaysRemaining = Number(purchase_days_remaining) || 0;

    const minBidAmount = !parseInt(highBidAmt)
        ? parseInt(start_price)
        : parseInt(highBidAmt ?? start_price) + parseInt(bid_increments);

    const syncEmitData = useMemo(
        () => ({
            property_id,
            user_id: user?.user_id || "",
            auction_id,
            domain_id: 3,
        }),
        [property_id, user, auction_id]
    );

    useEffect(() => {
        if (!isConnected || !syncEmitData?.property_id) return;

        // isAuthenticated &&
        //     SocketService.send("leavePropertyRoom", {
        //         property_id: syncEmitData.property_id,
        //     });

        // Join the socket room
        !isAuthenticated
            ? SocketService.send("joinPropertyRoom", {
                  property_id: syncEmitData.property_id,
              })
            : SocketService.send("joinPropertyRoom", {
                  property_id: syncEmitData.property_id,
                  user_id: syncEmitData.user_id,
              });
    }, [isConnected, isAuthenticated, syncEmitData]);

    useEffect(() => {
        // ================
        // SocketService.send("checkBid", syncEmitData);
        SocketService.send("isCallCheckBid", {
            property_id: syncEmitData?.property_id,
        });
        SocketService.send("re-sync", syncEmitData);
        //eslint-disable-next-line
    }, [isPurchased, isForeFitted]);

    const handleReSync = useCallback(
        ({ data }) => {
            if (!data || !syncEmitData) return;

            const updatedData = {
                ...data,
                user_id: syncEmitData.user_id,
                property_id: syncEmitData.property_id || data.property_id,
                auction_id: syncEmitData.auction_id || data.auction_id,
            };

            // SocketService.send("checkBid", updatedData);
        },
        [syncEmitData]
    );

    const handleCheckBid = useCallback(
        ({ code, error, data }) => {
            if (error) {
                console.error(error);
                return;
            }
            // popup logic
            setShowPopUp(
                !data?.my_max_bid_val ||
                    (data?.is_last_bid_retracted && is_approved === 2)
            );

            const bidPlaced = sessionStorage.getItem("bidPlaced");

            setBiddingData((previous = {}) => {
                if (
                    previous &&
                    previous?.selected_highest_bid_user_id !==
                        data?.selected_highest_bid_user_id
                ) {
                    dispatch(fetchPropertyBidsHistory(id, 1, 0));
                }
                if (!bidPlaced) return data;

                const prevHigh = Number(previous?.high_bid_amt || 0);
                const newHigh = Number(data?.high_bid_amt || 0);

                const changed = Object.keys(data || {})?.some(
                    (key) => data?.[key] !== previous?.[key]
                );

                if (newHigh > prevHigh) {
                    sessionStorage.removeItem("bidPlaced");
                    dispatch(setShowPlaceBidModal(true));
                    dispatch(setModalStep(4));
                    return data;
                }

                if (changed) return data;

                return previous;
            });
        },
        [dispatch, is_approved]
    );
    useEffect(() => {
        if (!isConnected || !syncEmitData) return;

        SocketService.on("isCallCheckBid", ({ code, error, data }) => {
            if (error) {
                console.error(error);
                return;
            }
            // Initial sync
            SocketService.send("checkBid", syncEmitData);
        });

        // Attach listeners
        SocketService.on("re-sync", handleReSync);
        SocketService.on("checkBid", handleCheckBid);

        SocketService.send("isCallCheckBid", {
            property_id: syncEmitData?.property_id,
        });

        // Initial sync
        // ============================
        // SocketService.send("checkBid", syncEmitData);

        return () => {
            sessionStorage.removeItem("bidPlaced");
            SocketService.off("re-sync", handleReSync);
            SocketService.off("checkBid", handleCheckBid);
            SocketService.off("isCallCheckBid");
        };
    }, [isConnected, syncEmitData, handleReSync, handleCheckBid]);

    const isRetractBid = useSelector((state) => state.project.isRetractBid);
    const fireCheckBid = sessionStorage.getItem("fireCheckBid");
    useEffect(() => {
        if (isRetractBid || fireCheckBid) {
            if (fireCheckBid) sessionStorage.removeItem("fireCheckBid");
            SocketService.send("isCallCheckBid", {
                property_id: syncEmitData?.property_id,
            });
            // SocketService.send("checkBid", syncEmitData);
        }
    }, [isRetractBid, syncEmitData, fireCheckBid]);

    if (auctionStatus === 3 && biddingData?.auction_status === 1) {
        SocketService.send("checkAuction", { domain_id: DOMAIN });
    }
    const handleAuctionStatus = (statusValue) => setAuctionStatus(statusValue);
    const handlePlaceBidClick = (e) => {
        e.stopPropagation();
        !buyerPriceCalculator?.dataLoading &&
            dispatch(handleCalculateFinalPrice({ reset: true }));
        dispatch(setShowPlaceBidModal(true));
    };

    const handleRegisterInterestClick = () => {
        if (!user?.user_id) {
            navigate(`/sign-in?redirect=${location.pathname}`);
        } else {
            setShowRegisterInterestModal(true);
        }
    };

    const isBiddingDataEmpty = useMemo(
        () => !biddingData || Object.keys(biddingData).length === 0,
        [biddingData]
    );

    useDidMountEffect(() => {
        if (
            (!biddingData?.is_last_bid_retracted && is_approved !== 2) ||
            !auctionStatus ||
            auctionStatus === 3 ||
            isBiddingDataEmpty ||
            !showPopUp ||
            !is_bid_transaction ||
            is_approved !== 2 ||
            !reg_transaction_status
        ) {
            return;
        }
        if (!isConnected) {
            dispatch(handleSiteLoader(true));
            return;
        }
        dispatch(handleSiteLoader(false));
        const bidType =
            is_auto_bid_enabled && reg_auto_bid_amount
                ? "setAutoBid"
                : "addNewBid";
        const bidData = {
            auction_id,
            bid_amount: minBidAmount,
            domain_id: 3,
            property_id,
            registration_id,
            user_id: user?.user_id,
            bid_increment: bid_increments,
            ip_address: ip || "127.0.0.1",
            auto_bid_amount: reg_auto_bid_amount,
            min_bid_amount: parseInt(min_bid_amount ?? start_price),
        };
        if (sessionStorage.getItem("isRetractedBid")) {
            sessionStorage.removeItem("isRetractedBid");
            return;
        }
        if (reg_transaction_status === 34) {
            // SocketService.send(bidType, bidData);
        } else if (reg_transaction_status === 4) {
            // Handle other status if needed
        }
    }, [isConnected, isBiddingDataEmpty, auctionStatus, showPopUp]);

    const handleStartBuyingClick = () => {
        if (!user?.user_id) {
            navigate(`/sign-in?redirect=${location.pathname}`);
        } else {
            setShowStartBuyingModal(true);
        }
    };
    const handleForefitClick = () => {
        if (!user?.user_id) {
            navigate(`/sign-in?redirect=${location.pathname}`);
        } else {
            setShowForefitModal(true);
        }
    };
    const isPropertyCreator = agent_id == user?.user_id;

    useEffect(() => {
        if (prevAuctionStatus.current === 2 && auctionStatus === 3) {
            SocketService.send("isCallCheckBid", {
                property_id: syncEmitData?.property_id,
            });
            // SocketService.send("checkBid", syncEmitData);
        }
        prevAuctionStatus.current = auctionStatus;
        // eslint-disable-next-line
    }, [auctionStatus]);

    useEffect(() => {
        onDataChange?.({
            biddingData,
            auctionStatus,
        });
    }, [biddingData, auctionStatus]);

    useEffect(() => {
        if (showPlaceBidModal && buyerPriceCalculator.show) {
            dispatch(
                handleCalculateFinalPrice({
                    show: false,
                })
            );
        }
    }, [showPlaceBidModal]);

    useEffect(() => {
        setApprovalStatus(
            is_registered && isBidApproved === 2 && is_payment_completed
        );
    }, [is_registered, isBidApproved, is_payment_completed]);

    const handleBidActionClick = (e, isLot = false) => {
        if (!user?.user_id) {
            navigate(
                `/sign-in?redirect=${encodeURIComponent(location.pathname)}`
            );
            return;
        } else {
            // if (property_for == 2 && !isLot) {
            //     navigate(
            //         `/live-feed/${createSlug(id, `${property_name} ${country}`)}`
            //     );
            //     return;
            // }
            if (biddingData && biddingData?.auction_status === 2 && !isLot) {
                toast.info(
                    t(
                        "Auction has been stopped. No further bids will be accepted."
                    )
                );
                return;
            }
            if (
                (user?.user_account_verification ||
                    user?.is_account_verified) !== accountStatus?.success
            ) {
                if (isLot) {
                    toast.info(
                        t("Please verify your account before proceeding.")
                    );
                    navigate("/verify");
                } else {
                    setAccountVerifiedModal(true);
                }
                return;
            }
        }

        if (isRegistrationApproved && !isLot) {
            handlePlaceBidClick(e);
            return;
        }

        navigate("/bidder-registration", {
            state: {
                propertyId: id,
                depositAmount: deposit_amount,
                propertyName: property_name,
                propertyNameAR: property_name_ar,
                currentPathName: window.location.href,
                ...(!isLot && { propertyAuctionDate: start_date }),
                ...(isLot && { propertyAuctionDate: session_start_date }),
                propertyState: state,
                propertyCommunity: community,
                propertyCommunityAr: community_ar,
                ...(isLot && { selectedSessionID: session_id }),
                ...(isLot && { lotID: lot_id }),
            },
        });
    };

    const handleJoinLiveRoom = (e) => {
        if (!user?.user_id) {
            navigate(
                `/sign-in?redirect=${encodeURIComponent(location.pathname)}`
            );
            return;
        }
        // else {
        //     if (
        //         (user?.user_account_verification ||
        //             user?.is_account_verified) !== accountStatus?.success
        //     ) {
        //         // setAccountVerifiedModal(true);
        //         toast.info(t("Please verify your account before proceeding."))
        //         navigate("/verify")
        //         return;
        //     }
        // }

        navigate("/live-auction-details", {
            state: {
                propertyId: id,
                selectedSessionID: session_id,
                lotID: lot_id,
            },
        });
    };

    const registrationStatus = useMemo(
        () =>
            getRegistrationStatus({
                is_registered,
                approvalStatus,
                isBidApproved,
                is_payment_completed,
            }),
        [is_registered, approvalStatus, isBidApproved, is_payment_completed]
    );

    const isRegistrationPending =
        registrationStatus === BID_APPROVAL_STATUS_MAP.Pending;

    const isLeading =
        !isPropertyCreator &&
        isSeller === 0 &&
        auctionStatus !== 3 &&
        biddingData?.max_bidder_user_id === parseInt(user?.user_id);

    // Bidders who lost see "Sold" instead of the winner's "Awaiting Purchase".
    const isLosingBidder =
        !isPropertyCreator &&
        isSeller === 0 &&
        !!biddingData?.my_max_bid_val &&
        biddingData?.max_bidder_user_id !== parseInt(user?.user_id);
    const closingStatusLabel =
        isLosingBidder &&
        biddingData?.closing_statuss_name === "Winner Pending Decision"
            ? "Sold"
            : biddingData?.closing_statuss_name || listing_status_name;

    useLayoutEffect(() => {
        if (
            session_id &&
            user?.user_id &&
            Array.isArray(lots?.data) &&
            lots?.data?.[0]?.session_id !== session_id
        ) {
            dispatch(
                fetchLots({
                    session_id: session_id,
                    // page: 1,
                })
            );
        }
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            const scrollTop =
                window.pageYOffset || document.documentElement.scrollTop;
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;

            const distanceFromBottom =
                documentHeight - (scrollTop + windowHeight);

            if (scrollTop > 200 && distanceFromBottom > 400) {
                setIsFixed(true);
            } else {
                setIsFixed(false);
            }
        };

        window.addEventListener("scroll", handleScroll);
        handleScroll();

        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const lotsAction = [
        ...((session_status?.status_id === 37 ||
            (session_status?.status_id === 1 &&
                lot_status?.status_id == 1 &&
                lot_pause_resume == 0)) &&
        // don't show if registera bid started
        !(session_status?.status_id === 1 || session_status?.status_id === 41)
            ? [
                  {
                      label: "Register Interest",
                      onClick: handleRegisterInterestClick,
                      disabled: isRegisteredInterset,
                  },
              ]
            : []),
        ...// (session_status?.status_id === 1 || session_status?.status_id === 41)
        (session_status?.status_id === 41 &&
        // don't show if joined rood started
        !(
            session_status?.status_id === 1 &&
            lot_status?.status_id === 1 &&
            lot_pause_resume === 1
        )
            ? [
                  {
                      label:
                          is_registered && is_payment_completed
                              ? "Bidder Registration Completed"
                              : "Register to Bid",
                      onClick: handleBidActionClick,
                      disabled: is_registered && is_payment_completed,
                  },
              ]
            : []),
        ...// session_status?.status_id === 1 && lot_status?.status_id === 1 && lot_pause_resume === 1
        (session_status?.status_id === 1
            ? [
                  ...(is_payment_completed &&
                  is_registered &&
                  ![8, 9, 16, 35].includes(lot_status?.status_id)
                      ? [
                            {
                                label: "Join the Room",
                                onClick: handleJoinLiveRoom,
                                disabled: false,
                            },
                        ]
                      : []),
                  ...(!(is_payment_completed && is_registered) &&
                  ![8, 9, 16, 35].includes(lot_status?.status_id)
                      ? [
                            {
                                label: "Watch Now",
                                onClick: handleJoinLiveRoom,
                                disabled: false,
                            },
                        ]
                      : []),
              ]
            : []),
        // ...(
        //     // session_status?.status_id === 1 && lot_status?.status_id === 41 && lot_pause_resume === 1 ?
        //     [{
        //         label: "Watch Now",
        //         onClick: handleJoinLiveRoom,
        //         disabled: false
        //     }]
        //     //  : []
        // ),
    ];

    return (
        <div className="col-lg-4">
            <div
                className={`sidebar d-none d-md-block ${isFixed ? "sticky" : ""}`}
            >
                {
                    // property_for === 2 ? <div className="sideabr-bg">
                    //     {
                    //         lotsAction.map((item) => {
                    //             return <CustomButton
                    //                 label={
                    //                     t(item.label)
                    //                 }
                    //                 className="btn btn-primary"
                    //                 onClick={item.label === "Register to Bid" ? (e) => item.onClick(e, true) : item.onClick}
                    //                 disabled={item?.disabled}
                    //             />
                    //         })
                    //     }

                    // </div> :
                    <div className="sideabr-bg">
                        {!isPropertyCreator &&
                            auctionStatus === 1 &&
                            property_auction_data?.[0]
                                ?.sell_at_full_amount_status &&
                            property_auction_data?.[0]?.full_amount && (
                                <SecureProperty />
                            )}

                        <h5 className="pb10">{t("Auction Details")}</h5>

                        {!isPropertyCreator &&
                            isSeller === 0 &&
                            auctionStatus === 2 &&
                            isRegistrationPending && (
                                <CustomButton
                                    label="Pending Approval"
                                    className="btn-lgtred"
                                />
                            )}

                        {((auctionStatus === 3 &&
                            biddingData?.closing_statuss_name) ||
                            String(offerer_user_id) === user?.user_id) && (
                            <CustomButton
                                label={
                                    isForeFitted
                                        ? t("Forfeited")
                                        : t(closingStatusLabel)
                                }
                                className={
                                    isForeFitted ? "btn-lgtred" : "btn-lgtgreen"
                                }
                                disabled={true}
                            />
                        )}

                        {/* {!isPropertyCreator &&
                        isSeller === 0 &&
                        auctionStatus !== 3 &&
                        biddingData?.max_bidder_user_id ===
                        parseInt(user?.user_id) && (
                            <div className="leading-stripe success">
                                <i className="fa-solid fa-angles-up"></i>{" "}
                                {t("You’re Leading")}
                            </div>
                        )} */}

                        {isLeading ? (
                            <div className="leading-stripe success">
                                <i className="fa-solid fa-angles-up"></i>{" "}
                                {t("You’re Leading")}
                            </div>
                        ) : (
                            <></>
                        )}
                        {!isPropertyCreator &&
                            isSeller === 0 &&
                            auctionStatus !== 3 &&
                            user?.user_id &&
                            biddingData?.my_max_bid_val &&
                            biddingData?.max_bidder_user_id !==
                                parseInt(user?.user_id) && (
                                <div className="leading-stripe danger">
                                    <i className="fa-solid fa-angles-down"></i>{" "}
                                    {t("Out of the Lead")}
                                </div>
                            )}

                        <HighestBid
                            data={biddingData}
                            auctionStatus={auctionStatus}
                            isForeFitted={isForeFitted}
                            isLeading={
                                isLeading ||
                                (!isPropertyCreator &&
                                    isSeller === 0 &&
                                    auctionStatus !== 3 &&
                                    user?.user_id &&
                                    biddingData?.my_max_bid_val &&
                                    biddingData?.max_bidder_user_id !==
                                        parseInt(user?.user_id))
                            }
                        />


                        {/* {property_for !== 2 ?
                            biddingData ? (
                                <AuctionTimer
                                    onStatusChange={handleAuctionStatus}
                                    data={biddingData}
                                />
                            ) : (
                                <Shimmer
                                    type="rectangle"
                                    width="100%"
                                    height="20vh"
                                    borderRadius="5%"
                                />
                            ) : <></>} */}
                        {biddingData && (
                            <AuctionTimer
                                property_for={property_for}
                                onStatusChange={handleAuctionStatus}
                                data={biddingData}
                            />
                        )}

                        {auctionStatus !== 3 &&
                            Number(city) === ABUDHABICITYID && (
                                <ComparableProperties data={biddingData} />
                            )}
                        {isAuthenticated ? (
                            <PriceCalculator
                                property_id={biddingData?.property_id}
                            />
                        ) : (
                            <></>
                        )}

                        {!isPropertyCreator &&
                            isSeller === 0 &&
                            auctionStatus === 1 &&
                            property_for !== 2 &&
                            !isPrivateAuction &&
                            !isRegistrationAllowed && (
                                <CustomButton
                                    label="Register Interest"
                                    className="btn-primary"
                                    onClick={handleRegisterInterestClick}
                                    disabled={isRegisteredInterset}
                                />
                            )}
                        {((!isPropertyCreator &&
                            isSeller === 0 &&
                            auctionStatus === 2) ||
                            (auctionStatus === 1 && isRegistrationAllowed) ||
                            (!isPropertyCreator &&
                                isSeller === 0 &&
                                auctionStatus === 1 &&
                                isPrivateAuction)) &&
                            property_for !== 2 && (
                                <CustomButton
                                    label={
                                        isRegistrationApproved
                                            ? sessionStorage.getItem(
                                                  "bidPlaced"
                                              )
                                                ? "Processing..."
                                                : biddingData?.my_max_bid_val
                                                  ? t("Raise my bid")
                                                  : t(
                                                        property_for === 2
                                                            ? "Start Bidding"
                                                            : auctionStatus ===
                                                                1
                                                              ? "Bidder Registration Completed"
                                                              : "Place a Bid"
                                                    )
                                            : t(
                                                  isRegistrationPending
                                                      ? "Approval Pending"
                                                      : is_registered &&
                                                          isBidApproved === 2
                                                        ? "Bidder Registration Completed"
                                                        : "Register As Bidder"
                                              )
                                    }
                                    {...(isRegistrationApproved && {
                                        image: "/img/raise-icon.svg",
                                    })}
                                    className="btn btn-primary"
                                    disabled={
                                        (isRegistrationApproved &&
                                            auctionStatus === 1) ||
                                        isRegistrationPending
                                    }
                                    onClick={(e) => handleBidActionClick(e)}
                                />
                            )}

                        {showFinalHighestBidderMessage && purchaseDeadline && (
                            <div className="purchase-deadline">
                                <p>
                                    {t("Complete your property purchase by")}
                                    <br />
                                    {purchaseDeadline}
                                </p>
                                <h6>
                                    {t(
                                        purchaseDaysRemaining === 1
                                            ? "day remaining"
                                            : "days remaining",
                                        {
                                            count: formatNumberByLocale(
                                                purchaseDaysRemaining,
                                                lang
                                            ),
                                        }
                                    )}
                                </h6>
                            </div>
                        )}

                        {/* Proceed / Forfeit buttons hidden; replaced by the
                            purchase-deadline box above. Original markup preserved:

                        {showFinalHighestBidderMessage && (
                            <>
                                <CustomButton
                                    onClick={handleStartBuyingClick}
                                    label={t(
                                        isPurchased
                                            ? "Purchase Requested"
                                            : "Proceed"
                                    )}
                                    className="btn-primary"
                                    disabled={isPurchased || isForeFitted}
                                />
                                {isPurchased ? (
                                    <></>
                                ) : (
                                    <CustomButton
                                        onClick={handleForefitClick}
                                        label={t(
                                            isForeFitted
                                                ? "Forfeit Requested"
                                                : "Forfeit"
                                        )}
                                        parentClassName="mbottom"
                                        className="btn-sky"
                                        disabled={isPurchased || isForeFitted}
                                    />
                                )}
                            </>
                        )}
                        */}
                        {property_for === 2 ? (
                            lotsAction.map((item) => {
                                return (
                                    <CustomButton
                                        label={t(`${item.label}`)}
                                        className="btn btn-primary"
                                        onClick={
                                            item.label === "Register to Bid"
                                                ? (e) => item.onClick(e, true)
                                                : item.onClick
                                        }
                                        disabled={item?.disabled}
                                    />
                                );
                            })
                        ) : (
                            <></>
                        )}

                        {((isSeller === 0 && !showFinalHighestBidderMessage) ||
                            (isSeller === 1 &&
                                auctionStatus === 3 &&
                                (isReserveMet || is_selected_highest_bid))) && (
                            <>
                                <div className="contact-btn last">
                                    <button
                                        onClick={() =>
                                            dispatch(handleContactUsModal(true))
                                        }
                                        className="btn btn-sm btn-full btn-white mbottom"
                                    >
                                        {t("Contact Us")}
                                    </button>
                                    {/* ADREC: the "Why <brand>?" button is removed. It
                                        scrolled to #whyPropify, the id on the
                                        Buying-with-brand section that has been removed
                                        from Details.jsx, so it had nothing left to
                                        scroll to. The Contact Us button above is
                                        unrelated and stays. Original markup preserved:

                                    <button
                                        className="btn btn-sm btn-full btn-white mbottom"
                                        onClick={() => {
                                            const section =
                                                document.getElementById(
                                                    "whyPropify"
                                                );
                                            if (section) {
                                                section.scrollIntoView({
                                                    behavior: "smooth",
                                                });
                                            }
                                        }}
                                    >
                                        {t("Why Propify?")}
                                    </button>
                                    */}
                                </div>
                                {/* <CustomButton
                                    label="Contact Us"
                                    parentClassName={
                                        isSeller !== 1 &&
                                            (!isReserveMet || !is_selected_highest_bid)
                                            ? "mbottom"
                                            : ""
                                    }
                                    className={`${isSeller ? "btn-primary" : "btn-white"} mbottom`}
                                    onClick={() => dispatch(handleContactUsModal(true))}
                                /> */}
                            </>
                        )}
                        {/* {isSeller === 0 && auctionStatus !== 3 && <HelpSection />} */}
                    </div>
                }
            </div>

            {/* Sticky bottom action bar — phones only (<768px). The full
                card above stays as-is for tablet/desktop; on phones this
                condenses the same status/bid/timer/CTA data already
                computed above into a persistent bottom bar. */}
            {biddingData && (
                <div
                    className="mobile-bid-bar d-md-none"
                    dir={lang === "ar" ? "rtl" : "ltr"}
                >
                    {(() => {
                        let statusPill = null;
                        if (
                            !isPropertyCreator &&
                            isSeller === 0 &&
                            auctionStatus === 2 &&
                            isRegistrationPending
                        ) {
                            statusPill = {
                                label: t("Pending Approval"),
                                className: "btn-lgtred",
                            };
                        } else if (
                            (auctionStatus === 3 &&
                                biddingData?.closing_statuss_name) ||
                            String(offerer_user_id) === user?.user_id
                        ) {
                            statusPill = {
                                label: isForeFitted
                                    ? t("Forfeited")
                                    : t(closingStatusLabel),
                                className: isForeFitted
                                    ? "btn-lgtred"
                                    : "btn-lgtgreen",
                            };
                        } else if (isLeading) {
                            statusPill = {
                                label: t("You’re Leading"),
                                className: "btn-lgtgreen",
                            };
                        } else if (
                            !isPropertyCreator &&
                            isSeller === 0 &&
                            auctionStatus !== 3 &&
                            user?.user_id &&
                            biddingData?.my_max_bid_val &&
                            biddingData?.max_bidder_user_id !==
                                parseInt(user?.user_id)
                        ) {
                            statusPill = {
                                label: t("Out of the Lead"),
                                className: "btn-lgtred",
                            };
                        }

                        // Same action buttons/labels as the full card above,
                        // condensed to at most two slots (Price always fills
                        // the first slot; Contact Us fills a spare third
                        // slot when only one primary action applies).
                        const actions = [];
                        if (
                            !isPropertyCreator &&
                            isSeller === 0 &&
                            auctionStatus === 1 &&
                            property_for !== 2 &&
                            !isPrivateAuction &&
                            !isRegistrationAllowed
                        ) {
                            actions.push({
                                label: t("Register Interest"),
                                onClick: handleRegisterInterestClick,
                                disabled: isRegisteredInterset,
                            });
                        }
                        if (
                            ((!isPropertyCreator &&
                                isSeller === 0 &&
                                auctionStatus === 2) ||
                                (auctionStatus === 1 &&
                                    isRegistrationAllowed) ||
                                (!isPropertyCreator &&
                                    isSeller === 0 &&
                                    auctionStatus === 1 &&
                                    isPrivateAuction)) &&
                            property_for !== 2
                        ) {
                            actions.push({
                                label: isRegistrationApproved
                                    ? sessionStorage.getItem("bidPlaced")
                                        ? t("Processing...")
                                        : biddingData?.my_max_bid_val
                                          ? t("Raise my bid")
                                          : t(
                                                property_for === 2
                                                    ? "Start Bidding"
                                                    : auctionStatus === 1
                                                      ? "Bidder Registration Completed"
                                                      : "Place a Bid"
                                            )
                                    : t(
                                          isRegistrationPending
                                              ? "Approval Pending"
                                              : is_registered &&
                                                  isBidApproved === 2
                                                ? "Bidder Registration Completed"
                                                : "Register As Bidder"
                                      ),
                                onClick: (e) => handleBidActionClick(e),
                                disabled:
                                    (isRegistrationApproved &&
                                        auctionStatus === 1) ||
                                    isRegistrationPending,
                            });
                        }
                        // Proceed / Forfeit hidden, same as the full card.
                        /* if (showFinalHighestBidderMessage) {
                            actions.push({
                                label: isPurchased
                                    ? t("Purchase Requested")
                                    : t("Proceed"),
                                onClick: handleStartBuyingClick,
                                disabled: isPurchased || isForeFitted,
                            });
                            if (!isPurchased) {
                                actions.push({
                                    label: isForeFitted
                                        ? t("Forfeit Requested")
                                        : t("Forfeit"),
                                    onClick: handleForefitClick,
                                    disabled: isPurchased || isForeFitted,
                                });
                            }
                        } */
                        if (property_for === 2) {
                            lotsAction.forEach((item) =>
                                actions.push({
                                    label: t(item.label),
                                    onClick:
                                        item.label === "Register to Bid"
                                            ? (e) => item.onClick(e, true)
                                            : item.onClick,
                                    disabled: item?.disabled,
                                })
                            );
                        }

                        const primaryAction = actions[0];
                        const secondaryAction = actions[1] || {
                            label: t("Contact Us"),
                            onClick: () => dispatch(handleContactUsModal(true)),
                        };

                        return (
                            <>
                                {statusPill && (
                                    <div
                                        className={`mobile-status-pill btn ${statusPill.className}`}
                                    >
                                        {statusPill.label}
                                    </div>
                                )}
                                <div className="mobile-stats-row">
                                    <HighestBid
                                        compact
                                        data={biddingData}
                                        auctionStatus={auctionStatus}
                                        isForeFitted={isForeFitted}
                                        isLeading={isLeading}
                                    />
                                    <div className="mobile-stat">
                                        <span className="mobile-stat-label">
                                            {t("Deposit")}
                                        </span>
                                        <strong className="mobile-stat-value">
                                            {t("Amount", {
                                                amount: formatPrice(
                                                    deposit_amount,
                                                    lang
                                                ),
                                            })}
                                        </strong>
                                    </div>
                                    <AuctionTimer
                                        compact
                                        property_for={property_for}
                                        data={biddingData}
                                    />
                                </div>
                                <div className="mobile-actions-row">
                                    <button
                                        type="button"
                                        className="btn mobile-action-btn outline"
                                        onClick={() => {
                                            dispatch(
                                                handleCalculateFinalPrice({
                                                    show: true,
                                                })
                                            );
                                            dispatch(
                                                calFinalPrice(
                                                    {
                                                        property_id:
                                                            biddingData?.property_id,
                                                    },
                                                    t
                                                )
                                            );
                                        }}
                                    >
                                        {t("Price")}
                                    </button>
                                    {primaryAction && (
                                        <button
                                            type="button"
                                            className="btn mobile-action-btn solid"
                                            onClick={primaryAction.onClick}
                                            disabled={primaryAction.disabled}
                                        >
                                            {primaryAction.label}
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="btn mobile-action-btn outline"
                                        onClick={secondaryAction.onClick}
                                        disabled={secondaryAction.disabled}
                                    >
                                        {secondaryAction.label}
                                    </button>
                                </div>
                            </>
                        );
                    })()}
                </div>
            )}

            {isConnected && biddingData && auctionStatus !== 3 && (
                <PlaceBidModal
                    show={showPlaceBidModal}
                    onClose={() => dispatch(setShowPlaceBidModal(false))}
                    data={biddingData}
                    user={user}
                    auction_id={auction_id}
                />
            )}

            {(auctionStatus === 1 ||
                session_status?.status_id === 37 ||
                (session_status?.status_id === 1 &&
                    lot_status?.status_id == 1 &&
                    lot_pause_resume == 0)) && (
                <RegisterInterestModal
                    show={showRegisterInterestModal}
                    onClose={() => setShowRegisterInterestModal(false)}
                    property={id}
                    setIsRegisteredInterest={setIsRegisteredInterest}
                    rememberMeFlag={is_remember_interest}
                />
            )}

            <VerifyAccountModal
                show={accountVerifiedModal}
                onClose={() => setAccountVerifiedModal(false)}
            />

            <StartBuyingProcessModal
                show={showStartBuyingModal}
                onClose={() => setShowStartBuyingModal(false)}
                property={property_id}
                isPurchased={isPurchased}
                setIsPurchased={setIsPurchased}
                setIsForeFitted={setIsForeFitted}
                bidPrice={biddingData?.my_max_bid_val}
            />

            <ForefitModal
                show={showForefitModal}
                onClose={() => setShowForefitModal(false)}
                property={property_id}
                isForeFitted={isForeFitted}
                setIsPurchased={setIsPurchased}
                setIsForeFitted={setIsForeFitted}
                bidPrice={biddingData?.my_max_bid_val}
            />
            {user?.user_id && isSeller === 0 && property_for !== 2 && (
                <section id="history">
                    <BidHistory
                        propertyId={id}
                        auctionStatus={auctionStatus}
                        isBidder={biddingData?.my_max_bid_val}
                        start_date={biddingData?.start_date}
                        end_date={biddingData?.end_date}
                        show_reverse_not_met={
                            property_setting
                                ? property_setting?.show_reverse_not_met
                                : false
                        }
                        isReserveMet={isReserveMet}
                        isForeFitted={isForeFitted}
                    />
                </section>
            )}
        </div>
    );
};

export default AuctionBidding;

const getRegistrationStatus = ({
    is_registered,
    approvalStatus,
    isBidApproved,
    is_payment_completed,
}) =>
    !is_registered
        ? "unknown"
        : approvalStatus && isBidApproved === 2
          ? "success"
          : !approvalStatus && is_payment_completed && isBidApproved === 1
            ? "pending"
            : !approvalStatus && isBidApproved === 3
              ? "rejected"
              : "unknown";
